import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, Pencil } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import MainSidebarContent from '../components/layout/MainSidebarContent';
import TopBar from '../components/layout/TopBar';
import AgentDetailsDrawer, { type AgentDetails } from '../features/agent-details/AgentDetailsDrawer';
import { useAuthStore } from '../stores/authStore';
import { useTaskStore } from '../stores/taskStore';
import api from '../services/api';

export default function AgentStorePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { tasks, setTasks } = useTaskStore();
  // ?agent=<task_name> opens the details drawer, so a view can be linked to.
  const [searchParams, setSearchParams] = useSearchParams();
  // The API decides who may see an agent's configuration (can_view_details).
  const viewableAgents = (tasks as unknown as AgentDetails[]).filter((t) => t.can_view_details);
  const viewingAgent = viewableAgents.find((t) => t.task_name === searchParams.get('agent'));
  const [confirmDeleteFor, setConfirmDeleteFor] = useState<string | null>(null);
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [canManageAgents, setCanManageAgents] = useState(false);
  const [managedGroups, setManagedGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  useEffect(() => {
    loadTheme();
    checkAdmin();
    loadAgents();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openMenu) setOpenMenu(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenu]);

  const loadAgents = async () => {
    try {
      const res = await api.get('/api/datasets');
      const list = res.data.agents || [];
      
      // Deduplicate by task_name
      const uniqueAgents = list.reduce((acc: any[], agent: any) => {
        if (!acc.find(a => a.task_name === agent.task_name)) {
          acc.push(agent);
        }
        return acc;
      }, []);
      
      setTasks(uniqueAgents);
    } catch (err) {
      console.error('Failed to load agents:', err);
    }
  };

  const checkAdmin = async () => {
    try {
      const res = await api.get('/api/me/role');
      const canManage = !!res.data?.can_manage_agents;
      setCanManageAgents(canManage);
      setManagedGroups(res.data?.managed_groups || []);
    } catch (err: any) {
      // No fallback: if the role can't be read, the user gets no manager UI.
      console.error('Failed to check permissions:', err);
      setCanManageAgents(false);
    } finally {
      setLoading(false);
    }
  };

  const loadTheme = () => {
    const saved = localStorage.getItem('fm-theme') as 'dark' | 'light' | null;
    if (saved) setTheme(saved);
  };

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    localStorage.setItem('fm-theme', next);
  };

  const filteredAgents = viewableAgents.filter((agent) => {
    const q = searchQuery.toLowerCase();
    return (
      agent.display_name?.toLowerCase().includes(q) ||
      agent.task_name.toLowerCase().includes(q) ||
      agent.bq_dataset?.toLowerCase().includes(q) ||
      agent.description?.toLowerCase().includes(q)
    );
  });

  const openAgent = (taskName: string, confirmDelete = false) => {
    setOpenMenu(null);
    setConfirmDeleteFor(confirmDelete ? taskName : null);
    setSearchParams({ agent: taskName }, { replace: true });
  };

  const closeAgent = () => {
    setConfirmDeleteFor(null);
    setSearchParams({}, { replace: true });
  };

  const handleEdit = (taskName: string) => {
    navigate(`/settings/editagent?task=${encodeURIComponent(taskName)}`);
  };

  const handleDuplicate = (agent: any) => {
    setOpenMenu(null);
    
    // Create a copy with modified display name and cleared Slack settings
    const duplicatedAgent = {
      ...agent,
      task_name: '', // Will be auto-generated from display_name
      display_name: `${agent.display_name} (Copy)`,
      digest_enabled: false, // Turn off Slack digest
      slack_channel: '', // Clear Slack channel - user will select
      slack_channel_id: '', // Clear Slack channel ID
      schedule: '', // Clear schedule
      // Copied queries aren't verified for the new tables until tested again
      premade_queries: (agent.premade_queries || []).map((q: any) => ({ ...q, verified: false })),
      // Keep all other fields including context, tables, etc.
    };
    
    // Navigate to add agent page (not edit) with pre-populated data
    navigate('/settings/addagent', { state: { editAgent: duplicatedAgent } });
  };

  // Called from the drawer's inline confirmation; returns an error message or null.
  const handleDelete = async (agent: AgentDetails): Promise<string | null> => {
    try {
      await api.delete(`/api/datasets/${encodeURIComponent(agent.task_name)}`);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      return typeof detail === 'string' ? detail : 'Could not delete the agent. Please try again.';
    }
    closeAgent();
    await loadAgents();
    return null;
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        height: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--background)',
        color: 'var(--text)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '48px', height: '48px', color: 'var(--accent)', animation: 'spin 1s linear infinite' }}>
              <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" strokeLinecap="round"/>
            </svg>
          </div>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Loading...</p>
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Plain viewers don't get agent details; managers and group admins do.
  if (!canManageAgents && managedGroups.length === 0) {
    return (
      <div style={{
        display: 'flex',
        height: '100vh',
        background: 'var(--background)',
        color: 'var(--text)'
      }}>
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)}>
          <MainSidebarContent
            currentPage="agents"
            onNewChat={() => navigate('/')}
            onShowHistory={() => {/* No-op since history is in main sidebar */}}
          />
        </Sidebar>

        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <TopBar
            sidebarOpen={sidebarOpen}
            onToggleSidebar={() => setSidebarOpen(true)}
            theme={theme}
            onToggleTheme={toggleTheme}
            user={user}
          />

          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px'
          }}>
            <div style={{ textAlign: 'center', maxWidth: '400px' }}>
              <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: '80px', height: '80px', color: 'var(--text-muted)', opacity: 0.6 }}>
                  <rect x="5" y="11" width="14" height="10" rx="2" />
                  <path d="M12 17v-2" />
                  <circle cx="12" cy="17" r="0.5" fill="currentColor" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
              </div>
              <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '12px' }}>Permission Required</h2>
              <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '24px', lineHeight: '1.6' }}>
                You need agent manager or admin permissions, or to be an admin of a group with agent access, to open the Agent Store. Please contact an administrator if you need access.
              </p>
              <button
                type="button"
                onClick={() => navigate('/')}
                style={{
                  padding: '10px 20px',
                  background: 'var(--accent)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'opacity 0.15s'
                }}
                onMouseOver={(e) => { e.currentTarget.style.opacity = '0.85'; }}
                onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
              >
                Back to Home
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      background: 'var(--background)',
      color: 'var(--text)'
    }}>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      >
        <MainSidebarContent
          currentPage="agents"
          onNewChat={() => navigate('/')}
          onShowHistory={() => {/* No-op since history is in main sidebar */}}
        />
      </Sidebar>

      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(true)}
          theme={theme}
          onToggleTheme={toggleTheme}
          user={user}
        />

        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '32px 48px'
        }}>
          {/* Header */}
          <div style={{ marginBottom: '32px' }}>
            <h1 style={{
              fontSize: '28px',
              fontWeight: 600,
              marginBottom: '8px',
              color: 'var(--text)'
            }}>
              Agent Store
            </h1>
            <p style={{
              fontSize: '14px',
              color: 'var(--text-muted)',
              marginBottom: '24px'
            }}>
              Browse the data agents you have access to. Open one to see what it knows.
            </p>

            {/* Search */}
            <div style={{ position: 'relative', maxWidth: '500px' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '18px',
                height: '18px',
                color: 'var(--text-muted)'
              }}>
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Search agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 44px',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  color: 'var(--text)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  outline: 'none',
                  transition: 'border-color 0.15s'
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
              />
            </div>
          </div>

          {/* Agent Grid */}
          {filteredAgents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>
              {searchQuery ? 'No agents found matching your search' : 'No agents available'}
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: '20px'
            }}>
              {filteredAgents.map((agent) => (
                <div
                  key={agent.task_name}
                  role="button"
                  tabIndex={0}
                  onClick={() => openAgent(agent.task_name)}
                  onKeyDown={(e) => {
                    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      openAgent(agent.task_name);
                    }
                  }}
                  style={{
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: '14px',
                    padding: '20px',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, transform 0.15s',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    position: 'relative'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = 'var(--accent)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Header Row: Icon and Menu */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    {/* Agent Icon */}
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        background: 'var(--surface2)',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" style={{ width: '22px', height: '22px' }}>
                        <rect x="5" y="8" width="14" height="10" rx="3"/>
                        <path d="M9 8V7a3 3 0 0 1 6 0v1"/>
                        <circle cx="9.5" cy="13" r="1" fill="var(--accent)" stroke="none"/>
                        <circle cx="14.5" cy="13" r="1" fill="var(--accent)" stroke="none"/>
                        <path d="M12 2v2M8 4l1.2 1.2M16 4l-1.2 1.2"/>
                      </svg>
                    </div>

                    {/* Three-dot Menu Button */}
                    {canManageAgents && (
                      <div style={{ position: 'relative' }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenu(openMenu === agent.task_name ? null : agent.task_name);
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--text-muted)',
                            borderRadius: '6px',
                            transition: 'all 0.15s'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = 'var(--surface2)';
                            e.currentTarget.style.color = 'var(--text)';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = 'var(--text-muted)';
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '20px', height: '20px' }}>
                            <circle cx="12" cy="5" r="1.5"/>
                            <circle cx="12" cy="12" r="1.5"/>
                            <circle cx="12" cy="19" r="1.5"/>
                          </svg>
                        </button>

                        {/* Dropdown Menu */}
                        {openMenu === agent.task_name && (
                          <div
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: '32px',
                              background: 'var(--surface)',
                              border: '1px solid var(--border)',
                              borderRadius: '8px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              minWidth: '160px',
                              zIndex: 1000,
                              overflow: 'hidden'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {[
                              { label: 'View details', Icon: Eye, onClick: () => openAgent(agent.task_name) },
                              { label: 'Edit agent', Icon: Pencil, onClick: () => handleEdit(agent.task_name) },
                            ].map(({ label, Icon, onClick }) => (
                              <button
                                key={label}
                                onClick={onClick}
                                style={{
                                  width: '100%',
                                  padding: '10px 16px',
                                  background: 'transparent',
                                  border: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  color: 'var(--text)',
                                  fontSize: '14px',
                                  transition: 'background 0.15s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface2)'; }}
                                onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              >
                                <Icon size={16} />
                                {label}
                              </button>
                            ))}
                            <button
                              onClick={() => handleDuplicate(agent)}
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                background: 'transparent',
                                border: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                color: 'var(--text)',
                                fontSize: '14px',
                                transition: 'background 0.15s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                              onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface2)'; }}
                              onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                                <rect x="9" y="9" width="13" height="13" rx="2"/>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                              </svg>
                              Duplicate agent
                            </button>
                            <div style={{ height: '1px', background: 'var(--border)', margin: '0' }}></div>
                            <button
                              onClick={() => openAgent(agent.task_name, true)}
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                background: 'transparent',
                                border: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                color: '#ef4444',
                                fontSize: '14px',
                                transition: 'background 0.15s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                              onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface2)'; }}
                              onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              </svg>
                              Delete agent
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Agent Name */}
                  <div>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: 'var(--text)',
                      marginBottom: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {agent.display_name || agent.task_name}
                    </div>
                  </div>

                  {/* Description */}
                  {agent.description && (
                    <div style={{
                      fontSize: '13px',
                      color: 'var(--text-muted)',
                      lineHeight: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {agent.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {viewingAgent && (
        <AgentDetailsDrawer
          agent={viewingAgent}
          canManage={canManageAgents}
          confirmDeleteOnOpen={confirmDeleteFor === viewingAgent.task_name}
          onClose={closeAgent}
          onStartChat={() => navigate(`/?agent=${encodeURIComponent(viewingAgent.task_name)}`)}
          onEdit={() => handleEdit(viewingAgent.task_name)}
          onDuplicate={() => handleDuplicate(viewingAgent)}
          onDelete={() => handleDelete(viewingAgent)}
        />
      )}
    </div>
  );
}
